/*
static:
  MINIMUM_CONFIGURABLE_PDFJS_SCALE
  MAXIMUM_CONFIGURABLE_PDFJS_SCALE: when clicking +, can't go beyond this value

user configured (with defaults):
  PDFJS_SCALE: 2

rendered (what we actually use):
  RENDERED_PDFJS_SCALE: scale that we feed PDF.js, relative to PDF's size that would render on page by default

computed (what we get from the DOM):
  WINDOW_WIDTH_PIXEL
  RENDERED_VISIBLE_PAGE_WIDTH
*/

import {
  GET_DOCUMENT_ERROR,
  GET_DOCUMENT_PENDING,
  GET_DOCUMENT_SUCCESS,
  GET_PAGE_ERROR,
  GET_PAGE_PENDING,
  GET_PAGE_SUCCESS,
  GET_TEXT_CONTENT_ERROR,
  GET_TEXT_CONTENT_PENDING,
  GET_TEXT_CONTENT_SUCCESS,
  SCALE_PAGE,
  SET_PAGE_ROTATION,
  SET_ALL_PAGE_ROTATIONS,
  SET_SCALE,
} from './constants'
import { MAX_SCALE, MIN_SCALE, DEFAULT_SCALE } from '../constants'

const initialState = {
  documentLoadError: null,
  documentId: null,
  pdfDocument: null,
  wasPageLoadedSuccessfully: false,
  pageLoadError: null,
  page: null,
  textContent: null,
  scale: DEFAULT_SCALE,
  numPages: 0,
  pdfRotationsByPage: {},
}

function calculateScale(current, modifier) {
  const newScale = Math.round((current + modifier) * 100) / 100

  return newScale >= MIN_SCALE && newScale <= MAX_SCALE ? newScale : current
}

export default function pdfViewerReducers(state = initialState, action) {
  switch (action.type) {
    case GET_DOCUMENT_SUCCESS:
      const { pdfDocument, documentId } = action
      const newState = {
        ...state,
        documentLoadError: null,
        documentId,
        pdfDocument,
        numPages: pdfDocument._pdfInfo.numPages,
      }
      return newState

    case SET_SCALE:
      let newScale = action.scaleRatio * state.scale
      if (newScale > 0.9 && newScale < 1.1) {
        return state
      }
      // When new scale value is greater than 1.5 and the current scale is
      // already at 1.5, we do not continue to scale up the document

      // We need default scale either when initially loaded, and user
      //   has not zoomed in/out. Continues to apply after comments deleted
      //   or window is resized but user has not zoomed in/out.

      if (newScale > DEFAULT_SCALE && state.scale === DEFAULT_SCALE) {
        return {
          ...state,
          viewport: state.page && state.page.getViewport({ scale: state.scale }),
        }
        // This is to alleviate drastic scale resizing
      } else if (newScale > DEFAULT_SCALE) {
        newScale = DEFAULT_SCALE
        // hard minimum scale set
      } else if (newScale < MIN_SCALE) {
        newScale = MIN_SCALE
      }

      return {
        ...state,
        scale: newScale,
        viewport: state.page && state.page.getViewport({ scale: state.scale }),
      }

    case SET_PAGE_ROTATION:
      return {
        ...state,
        pdfRotationsByPage: {
          ...state.pdfRotationsByPage,
          [action.pdfPageNumber]: action.pdfPageRotation,
        },
      }

    case SET_ALL_PAGE_ROTATIONS:
      return {
        ...state,
        pdfRotationsByPage: {
          ...state.pdfRotationsByPage,
          ...action.pdfRotationsByPage,
        },
      }

    case SCALE_PAGE:
      const scale = calculateScale(state.scale, action.scaleModifier)

      // return state since scale hasn't changed.
      if (scale === state.scale) {
        return state
      }

      return {
        ...state,
        scale,
        viewport: state.page && state.page.getViewport({ scale: state.scale }),
      }

    case GET_DOCUMENT_PENDING:
      return {
        ...state,
        documentLoadError: null,
        pdfDocument: null,
      }

    case GET_DOCUMENT_ERROR:
      return {
        ...state,
        documentLoadError: action.error,
        pdfDocument: null,
      }

    case GET_PAGE_PENDING:
      return {
        ...state,
        wasPageLoadedSuccessfully: false,
        pageLoadError: null,
        page: null,
      }

    case GET_PAGE_SUCCESS:
      const viewport = action.page.getViewport({ scale: state.scale })
      return {
        ...state,
        wasPageLoadedSuccessfully: true,
        pageLoadError: null,
        page: action.page,
        viewport,
      }

    case GET_PAGE_ERROR:
      return {
        ...state,
        wasPageLoadedSuccessfully: false,
        pageLoadError: action.error,
        page: null,
      }

    case GET_TEXT_CONTENT_PENDING: {
      return {
        ...state,
        textContent: null,
      }
    }

    case GET_TEXT_CONTENT_SUCCESS:
      return {
        ...state,
        textContent: action.textContent,
      }

    case GET_TEXT_CONTENT_ERROR:
      return {
        ...state,
        textContent: null,
      }

    default:
      return state
  }
}
